import type { ProjectTaskStats } from '@hygge/shared';

/**
 * The project's boards in the order the project page lists them: by name. No
 * board is the project's own (HW-983, daily 2026-10-16) — every linked board
 * is an ordinary one.
 */
export function sortProjectBoards<T extends { name: string }>(boards: readonly T[] | undefined): T[] {
  return [...(boards ?? [])].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The boards archiving a project can act on (HW-983): those whose settings the
 * reader may change, which is all the server touches. Asking about any other
 * board would offer a choice that changes nothing.
 */
export function boardsReaderMaySet<T extends { currentUserPermissions: readonly string[] }>(
  boards: readonly T[] | undefined,
): T[] {
  return (boards ?? []).filter((board) => board.currentUserPermissions.includes('board.settings'));
}

/**
 * The task figures of several boards read as one (HW-983): the project's
 * Dashboard and Progress count the tasks of every linked board the reader can
 * open. Totals and categories add up; statuses of the same name and category
 * are one row, whatever board they come from, in the order first seen; the
 * completion share is worked out again from the sums, as the server works it
 * for one board. `undefined` for no boards — nothing to count is not zero.
 */
export function combineTaskStats(stats: readonly ProjectTaskStats[]): ProjectTaskStats | undefined {
  if (stats.length === 0) return undefined;
  if (stats.length === 1) return stats[0];
  const byCategory = { backlog: 0, active: 0, done: 0, cancelled: 0 };
  const byStatus: ProjectTaskStats['byStatus'] = [];
  const statusAt = new Map<string, number>();
  let total = 0;
  for (const s of stats) {
    total += s.total;
    byCategory.backlog += s.byCategory.backlog;
    byCategory.active += s.byCategory.active;
    byCategory.done += s.byCategory.done;
    byCategory.cancelled += s.byCategory.cancelled;
    for (const row of s.byStatus) {
      const key = `${row.category}\u0000${row.name.trim().toLowerCase()}`;
      const at = statusAt.get(key);
      if (at === undefined) {
        statusAt.set(key, byStatus.length);
        byStatus.push({ ...row });
      } else {
        byStatus[at] = { ...byStatus[at], count: byStatus[at].count + row.count };
      }
    }
  }
  const completionPct = total > 0 ? Math.round((byCategory.done / total) * 100) : 0;
  return { total, byCategory, byStatus, completionPct };
}
